import { memo, useCallback, useEffect, useRef } from 'react';

import { type IconComponent, ThumbsDownIcon, ThumbsUpIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { WorkspaceAgentMessageFeedbackCopyButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/feedback/WorkspaceAgentMessageFeedbackCopyButton';
import { WorkspaceAgentMessageFeedbackSent } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/feedback/WorkspaceAgentMessageFeedbackSent';

export type WorkspaceAgentMessageFeedbackRating = 'up' | 'down';

type WorkspaceAgentMessageFeedbackActionProps = {
  'data-testid'?: string;
  icon: IconComponent;
  label: string;
  onClick: () => void;
};

const WorkspaceAgentMessageFeedbackAction = memo(
  ({ 'data-testid': testId, icon, label, onClick }: WorkspaceAgentMessageFeedbackActionProps) => (
    <Tooltip label={label}>
      <IconButton
        adsIcon
        appearance="ghost"
        className="focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8"
        color="gray"
        data-testid={testId}
        icon={icon}
        label={label}
        onClick={onClick}
        size="extra-small"
      />
    </Tooltip>
  ),
);

WorkspaceAgentMessageFeedbackAction.displayName = 'WorkspaceAgentMessageFeedbackAction';

export type WorkspaceAgentMessageFeedbackActionsProps = {
  copyText: string;
  'data-testid'?: string;
  onRate: (rating: WorkspaceAgentMessageFeedbackRating) => void;
  rating: WorkspaceAgentMessageFeedbackRating | undefined;
};

/** Copy and thumbs row under an assistant message in the private panel. */
export const WorkspaceAgentMessageFeedbackActions = memo(
  ({ copyText, 'data-testid': testId, onRate, rating }: WorkspaceAgentMessageFeedbackActionsProps) => {
    const sentIndicatorRef = useRef<HTMLSpanElement>(null);
    const previousRatingRef = useRef(rating);

    const handleRateUp = useCallback(() => {
      onRate('up');
    }, [onRate]);

    const handleRateDown = useCallback(() => {
      onRate('down');
    }, [onRate]);

    const hasSentRating = rating === 'up' || rating === 'down';
    const showInteractiveThumbs = !hasSentRating;

    useEffect(() => {
      if (hasSentRating && rating !== previousRatingRef.current) {
        sentIndicatorRef.current?.focus();
      }

      previousRatingRef.current = rating;
    }, [hasSentRating, rating]);

    if (!copyText && !hasSentRating && !showInteractiveThumbs) {
      return null;
    }

    return (
      <fieldset
        aria-label="Response feedback"
        className="m-0 mt-1 flex h-8 min-w-0 items-center gap-2 border-0 p-0 pl-2 [overflow-anchor:none]"
        data-testid={testId}
      >
        {copyText ? <WorkspaceAgentMessageFeedbackCopyButton copyText={copyText} /> : null}
        {hasSentRating && rating ? <WorkspaceAgentMessageFeedbackSent rating={rating} ref={sentIndicatorRef} /> : null}
        {showInteractiveThumbs ? (
          <>
            <WorkspaceAgentMessageFeedbackAction
              data-testid="WORKSPACE_AGENT_MESSAGE_FEEDBACK_UP"
              icon={ThumbsUpIcon}
              label="Good response"
              onClick={handleRateUp}
            />
            <WorkspaceAgentMessageFeedbackAction
              data-testid="WORKSPACE_AGENT_MESSAGE_FEEDBACK_DOWN"
              icon={ThumbsDownIcon}
              label="Bad response"
              onClick={handleRateDown}
            />
          </>
        ) : null}
      </fieldset>
    );
  },
);

WorkspaceAgentMessageFeedbackActions.displayName = 'WorkspaceAgentMessageFeedbackActions';
